import { faCheck, faPencil } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { memo, useContext, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { personAvailabilityChannelName } from '@/channels/utils/channelNames';
import AppContext from '@/contexts/appContext';
import useAPI from '@/hooks/useAPI';
import { useChannelData } from '@/hooks/useChannel';
import PersonContext from '@/pages/Person/PersonContext';
import PersonAvailabilityRepository from '@/repositories/PersonAvailabilityRepository';
import { PersonAvailability } from '@/types/personAvailability';
import { Spinner, Typography } from '@/ui';
import { hasPermission, PEOPLE_AVAILABILITY_VIEW } from '@/utils/userPermission';

import { AvailabilitySummary } from './components/AvailabilitySummary';

interface Props {
  showEditAction?: boolean;
  /** Turn off where the surrounding card already names the section. */
  showHeader?: boolean;
  onNewNoteClick?: () => void;
}

const AvailabilitySummarySection = ({
  showEditAction = true,
  showHeader = true,
  onNewNoteClick
}: Props) => {
  const { permissions } = useContext(AppContext);
  const { person } = useContext(PersonContext);
  const availabilityRepo = PersonAvailabilityRepository();
  const [isFlashing, setIsFlashing] = useState(false);

  // Fetch availability data
  const {
    data: availabilityData,
    isPending,
    refetch
  } = useAPI({
    queryKey: ['availability', person.id],
    queryFn: async () => {
      const response = await availabilityRepo.listByPersonId({
        personId: person.id
      });
      return response.data;
    }
  });

  // Subscribe to WebSocket updates
  useChannelData({
    channelId: personAvailabilityChannelName(person.id),
    channelName: 'Person Availability',
    messageCallback: (message) => {
      if (message.name === 'availability_updated') {
        setIsFlashing(true);
        refetch();
      }
    }
  });

  // Clear flash effect after animation
  useEffect(() => {
    if (isFlashing) {
      const timer = setTimeout(() => {
        setIsFlashing(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isFlashing]);

  // Extract the first availability record
  const availability = useMemo((): PersonAvailability | null => {
    if (Array.isArray(availabilityData?.results) && availabilityData.results.length > 0) {
      return availabilityData.results[0];
    }
    return null;
  }, [availabilityData]);

  return (
    <div
      id="availability-summary-section"
      className={`rounded-lg border border-uniform-gray-200 bg-gray-50 p-2 transition-all duration-500 dark:border-uniform-gray-700 ${
        isFlashing ? 'ring-2 ring-green-500' : ''
      }`}>
      {showHeader && (
        <div className="mb-2 flex items-center justify-between border-b border-uniform-gray-200 pb-2 dark:border-uniform-gray-700">
          <div className="flex items-center gap-2">
            <Typography variant="subheading">Available hours</Typography>
            {isFlashing && (
              <FontAwesomeIcon icon={faCheck} className="h-4 w-4 text-green-600 animate-pulse" />
            )}
          </div>

          {showEditAction &&
            hasPermission({ permissions, requiredPermission: PEOPLE_AVAILABILITY_VIEW }) && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  aria-label="Add note"
                  onClick={onNewNoteClick}
                  className="rounded border border-gray-300 bg-white px-2 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100">
                  New Note
                </button>
                <Link
                  to={`/people/${person.id}/availability`}
                  aria-label="Edit member availability"
                  title="Edit member availability"
                  className="rounded px-2 py-1 text-gray-400 hover:text-gray-600">
                  <FontAwesomeIcon icon={faPencil} className="text-lg" />
                </Link>
              </div>
            )}
        </div>
      )}

      {isPending ? (
        <Spinner />
      ) : (
        <div>
          <AvailabilitySummary availability={availability} />
        </div>
      )}
    </div>
  );
};

export default memo(AvailabilitySummarySection);
